import React, { useState, useEffect } from 'react';
import './Dashboard.css';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // HR Analytics Mock Data
  const [hrData] = useState({
    totalEmployees: 247,
    activeEmployees: 235,
    onLeave: 8,
    newHires: 12,
    avgPerformanceScore: 4.2,
    payrollProcessed: 98,
    pendingReviews: 15,
    onboardingProgress: 85,
    employeeSatisfaction: 87,
    avgTenure: 3.5,
    turnoverRate: 8.2
  });

  // Medallion Architecture Pipeline Data
  const [pipelineData] = useState({
    bronze: {
      name: 'Bronze Layer',
      description: 'Raw Data Ingestion',
      status: 'active',
      records: 45230,
      sources: ['Paystubs', 'Leave Requests', 'Performance Reviews', 'Onboarding Forms'],
      lastUpdated: '2 minutes ago'
    },
    silver: {
      name: 'Silver Layer',
      description: 'Cleansed & Validated',
      status: 'processing',
      records: 44890,
      quality: 99.2,
      transformations: ['Data Cleaning', 'Deduplication', 'Validation', 'Standardization'],
      lastUpdated: '5 minutes ago'
    },
    gold: {
      name: 'Gold Layer',
      description: 'Analytics-Ready',
      status: 'ready',
      records: 44850,
      aggregations: ['Employee Metrics', 'Cost Analysis', 'Trend Predictions', 'KPI Dashboards'],
      lastUpdated: '10 minutes ago'
    }
  });

  // Employee Insights Data
  const [employeeInsights] = useState({
    topPerformers: [
      { name: 'Sarah Chen', department: 'Engineering', score: 4.9, projects: 8 },
      { name: 'Michael Roberts', department: 'Sales', score: 4.8, projects: 12 },
      { name: 'Emily Zhang', department: 'Product', score: 4.7, projects: 6 },
      { name: 'David Kim', department: 'Marketing', score: 4.6, projects: 9 }
    ],
    atRiskEmployees: 3,
    promotionReady: 18,
    trainingNeeded: 12,
    retentionScore: 92
  });


  // Cost Savings Data
  const [costSavings] = useState({
    automationSavings: 125000,
    recruitmentOptimization: 85000,
    trainingEfficiency: 45000,
    retentionImprovement: 210000,
    totalSavings: 465000,
    projectedAnnual: 650000
  });

  // Recent Activities
  const [recentActivities] = useState([
    { id: 1, type: 'onboarding', employee: 'Sarah Johnson', status: 'in-progress', department: 'Engineering', timestamp: new Date() },
    { id: 2, type: 'review', employee: 'Michael Chen', status: 'completed', department: 'Sales', timestamp: new Date() },
    { id: 3, type: 'leave', employee: 'Emma Davis', status: 'approved', department: 'Marketing', timestamp: new Date() },
    { id: 4, type: 'payroll', employee: 'James Wilson', status: 'processed', department: 'Finance', timestamp: new Date() },
    { id: 5, type: 'onboarding', employee: 'Lisa Anderson', status: 'pending', department: 'HR', timestamp: new Date() },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        setError(null);
      } catch (err) {
        setError('Failed to fetch HR analytics data');
        console.error('HR data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  const getActivityIcon = (type) => {
    const icons = {
      'onboarding': '👋',
      'review': '📊',
      'leave': '🏖️',
      'payroll': '💰',
      'performance': '⭐'
    };
    return icons[type] || '📄';
  };

  const getActivityColor = (status) => {
    const colors = {
      'completed': 'success',
      'approved': 'success',
      'processed': 'success',
      'in-progress': 'info',
      'pending': 'warning',
      'rejected': 'danger'
    };
    return colors[status] || 'primary';
  };

  const getPipelineStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'processing': 'info',
      'ready': 'primary',
      'error': 'danger'
    };
    return colors[status] || 'primary';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading HR Analytics Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Navigation Bar */}
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="dashboard-title">👥 HR Analytics Platform</h1>
          <div className="connection-status connected">
            <span className="status-dot"></span>
            Live Dashboard
          </div>
        </div>
        <div className="nav-right">
          <div className="dashboard-time">
            {currentTime.toLocaleDateString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </nav>

      <main className="dashboard-content">
        {/* Primary HR Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card primary animate-fade-in">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-value">{hrData.totalEmployees}</div>
              <div className="stat-label">Total Employees</div>
              <div className="stat-trend positive">+{hrData.newHires} this month</div>
            </div>
          </div>

          <div className="stat-card success animate-fade-in" style={{animationDelay: '0.1s'}}>
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-value">{hrData.activeEmployees}</div>
              <div className="stat-label">Active Employees</div>
              <div className="stat-trend neutral">{hrData.onLeave} on leave</div>
            </div>
          </div>

          <div className="stat-card info animate-fade-in" style={{animationDelay: '0.2s'}}>
            <div className="stat-icon">⭐</div>
            <div className="stat-info">
              <div className="stat-value">{hrData.avgPerformanceScore}</div>
              <div className="stat-label">Avg Performance</div>
              <div className="stat-trend positive">+0.3 vs last quarter</div>
            </div>
          </div>

          <div className="stat-card warning animate-fade-in" style={{animationDelay: '0.3s'}}>
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-value">{hrData.payrollProcessed}%</div>
              <div className="stat-label">Payroll Processed</div>
              <div className="stat-trend positive">On schedule</div>
            </div>
          </div>

          <div className="stat-card danger animate-fade-in" style={{animationDelay: '0.4s'}}>
            <div className="stat-icon">📋</div>
            <div className="stat-info">
              <div className="stat-value">{hrData.pendingReviews}</div>
              <div className="stat-label">Pending Reviews</div>
              <div className="stat-trend negative">5 overdue</div>
            </div>
          </div>
        </div>

        {/* Cost Savings Section */}
        <div className="cost-savings-section animate-fade-in">
          <h3 className="section-title">💵 Cost Savings & ROI</h3>
          <div className="cost-savings-grid">
            <div className="cost-card">
              <div className="cost-icon">🤖</div>
              <div className="cost-content">
                <div className="cost-value">${(costSavings.automationSavings/1000).toFixed(0)}K</div>
                <div className="cost-label">Automation Savings</div>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-icon">🎯</div>
              <div className="cost-content">
                <div className="cost-value">${(costSavings.recruitmentOptimization/1000).toFixed(0)}K</div>
                <div className="cost-label">Recruitment Optimization</div>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-icon">📚</div>
              <div className="cost-content">
                <div className="cost-value">${(costSavings.trainingEfficiency/1000).toFixed(0)}K</div>
                <div className="cost-label">Training Efficiency</div>
              </div>
            </div>
            <div className="cost-card">
              <div className="cost-icon">🔒</div>
              <div className="cost-content">
                <div className="cost-value">${(costSavings.retentionImprovement/1000).toFixed(0)}K</div>
                <div className="cost-label">Retention Improvement</div>
              </div>
            </div>
            <div className="cost-card total">
              <div className="cost-icon">💎</div>
              <div className="cost-content">
                <div className="cost-value">${(costSavings.totalSavings/1000).toFixed(0)}K</div>
                <div className="cost-label">Total YTD Savings</div>
                <div className="cost-projection">Projected Annual: ${(costSavings.projectedAnnual/1000).toFixed(0)}K</div>
              </div>
            </div>
          </div>
        </div>

        {/* Medallion Architecture Pipeline */}
        <div className="medallion-architecture animate-fade-in">
          <h3 className="section-title">🏗️ Data ETL Pipeline - Medallion Architecture</h3>
          <div className="pipeline-flow">
            {/* Bronze Layer */}
            <div className="pipeline-layer bronze">
              <div className="layer-header">
                <div className="layer-icon">🥉</div>
                <div className="layer-info">
                  <h4>{pipelineData.bronze.name}</h4>
                  <p>{pipelineData.bronze.description}</p>
                </div>
                <span className={`layer-status ${getPipelineStatusColor(pipelineData.bronze.status)}`}>
                  {pipelineData.bronze.status}
                </span>
              </div>
              <div className="layer-metrics">
                <div className="metric-item">
                  <span className="metric-label">Records Ingested</span>
                  <span className="metric-value">{pipelineData.bronze.records.toLocaleString()}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Last Updated</span>
                  <span className="metric-value">{pipelineData.bronze.lastUpdated}</span>
                </div>
              </div>
              <div className="layer-sources">
                {pipelineData.bronze.sources.map((source, idx) => (
                  <span key={idx} className="source-tag">{source}</span>
                ))}
              </div>
            </div>

            <div className="pipeline-arrow">→</div>

            {/* Silver Layer */}
            <div className="pipeline-layer silver">
              <div className="layer-header">
                <div className="layer-icon">🥈</div>
                <div className="layer-info">
                  <h4>{pipelineData.silver.name}</h4>
                  <p>{pipelineData.silver.description}</p>
                </div>
                <span className={`layer-status ${getPipelineStatusColor(pipelineData.silver.status)}`}>
                  {pipelineData.silver.status}
                </span>
              </div>
              <div className="layer-metrics">
                <div className="metric-item">
                  <span className="metric-label">Cleansed Records</span>
                  <span className="metric-value">{pipelineData.silver.records.toLocaleString()}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Data Quality</span>
                  <span className="metric-value">{pipelineData.silver.quality}%</span>
                </div>
              </div>
              <div className="layer-sources">
                {pipelineData.silver.transformations.map((transform, idx) => (
                  <span key={idx} className="source-tag">{transform}</span>
                ))}
              </div>
            </div>

            <div className="pipeline-arrow">→</div>

            {/* Gold Layer */}
            <div className="pipeline-layer gold">
              <div className="layer-header">
                <div className="layer-icon">🥇</div>
                <div className="layer-info">
                  <h4>{pipelineData.gold.name}</h4>
                  <p>{pipelineData.gold.description}</p>
                </div>
                <span className={`layer-status ${getPipelineStatusColor(pipelineData.gold.status)}`}>
                  {pipelineData.gold.status}
                </span>
              </div>
              <div className="layer-metrics">
                <div className="metric-item">
                  <span className="metric-label">Analytics Records</span>
                  <span className="metric-value">{pipelineData.gold.records.toLocaleString()}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Last Updated</span>
                  <span className="metric-value">{pipelineData.gold.lastUpdated}</span>
                </div>
              </div>
              <div className="layer-sources">
                {pipelineData.gold.aggregations.map((agg, idx) => (
                  <span key={idx} className="source-tag">{agg}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Dashboard Grid */}
        <div className="dashboard-main-grid">
          {/* Employee Insights */}
          <div className="insights-section animate-slide-in">
            <h3 className="section-title">💡 Employee Insights</h3>

            <div className="insights-summary">
              <div className="insight-card">
                <span className="insight-value">{employeeInsights.topPerformers.length}</span>
                <span className="insight-label">Top Performers</span>
              </div>
              <div className="insight-card warning">
                <span className="insight-value">{employeeInsights.atRiskEmployees}</span>
                <span className="insight-label">At Risk</span>
              </div>
              <div className="insight-card success">
                <span className="insight-value">{employeeInsights.promotionReady}</span>
                <span className="insight-label">Promotion Ready</span>
              </div>
              <div className="insight-card info">
                <span className="insight-value">{employeeInsights.retentionScore}%</span>
                <span className="insight-label">Retention Score</span>
              </div>
            </div>

            <div className="top-performers-list">
              <h4>🌟 Top Performers</h4>
              {employeeInsights.topPerformers.map((employee, idx) => (
                <div key={idx} className="performer-card">
                  <div className="performer-info">
                    <span className="performer-name">{employee.name}</span>
                    <span className="performer-dept">{employee.department}</span>
                  </div>
                  <div className="performer-metrics">
                    <span className="performer-score">⭐ {employee.score}</span>
                    <span className="performer-projects">{employee.projects} projects</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activities Section */}
          <div className="analytics-main-section">
            {/* Recent Activities */}
            <div className="pipeline-status-section animate-fade-in">
              <h3 className="section-title">🔔 Recent Activities</h3>
              <div className="pipeline-list">
                {recentActivities.map((activity, index) => (
                  <div
                    key={activity.id}
                    className="pipeline-item animate-fade-in"
                    style={{animationDelay: `${index * 0.05}s`}}
                  >
                    <div className="activity-icon">{getActivityIcon(activity.type)}</div>
                    <div className="pipeline-info">
                      <span className="pipeline-name">{activity.employee}</span>
                      <span className="pipeline-time">
                        {activity.department} • {activity.timestamp.toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <span className={`pipeline-badge ${getActivityColor(activity.status)}`}>
                      {activity.status.replace('-', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
